/**
 * Synthetic document fixtures for the Magic Capture Carer Capture V0 prototype.
 *
 * Source of truth: 2026-06-03 Mini PRD - Magic Capture Carer Capture V0.
 * Weighted to the confirmed deep-tier types (POA/LPA + benefits/DWP paperwork),
 * plus one generic-tier document (discharge summary) to show that capture
 * accepts any care document. All people and reference numbers are synthetic.
 *
 * The SVG page images double as the seed of the Set up eval set. The LPA's
 * page 4 deliberately contains the s10-witness-missing defect (the OPG
 * rejection pattern) for the future Mini PRD 3 issue-spotting demo - V0 does
 * not act on it.
 *
 * JAMES: the `suggestedActions` copy below is first-draft product voice.
 * It's the core interaction of V0 - rewrite freely, nothing else depends on
 * the wording.
 */
import lpaPage1 from './assets/lpa-page-1.svg';
import lpaPage2 from './assets/lpa-page-2.svg';
import lpaPage3 from './assets/lpa-page-3.svg';
import lpaPage4 from './assets/lpa-page-4.svg';
import dwpLetter from './assets/dwp-letter.svg';
import dischargeSummary from './assets/discharge-summary.svg';

export type CaptureMethod = 'photo' | 'gallery' | 'file';

/** Mini PRD tiering: every doc gets the generic treatment; a small set of
 * priority types gets deep treatment. */
export type DocumentTier = 'deep' | 'generic';

export interface SuggestedAction {
  id: string;
  title: string;
  description: string;
}

export interface CapturedDocument {
  id: string;
  fileName: string;
  fileSizeKb: number;
  pageCount: number;
  tier: DocumentTier;
  classification: string;
  /** Synthetic page images (SVG URLs). Used for pickers, session thumbnails
   * and - later - the live understanding pipeline input. */
  pageImages: string[];
  /** Plain-English "what it says" bullets. */
  summary: string[];
  /** "What this means for your situation" bullets. */
  meaning: string[];
  suggestedActions: SuggestedAction[];
  /** When true, the processing step fails (demo of the failure path). */
  willFail?: boolean;
  failReason?: string;
  /** True when the tester supplied their own file - the understanding shown
   * is a labelled stand-in until the live pipeline exists. */
  isUserProvided?: boolean;
}

let userFileCounter = 0;

/**
 * Wraps a file the tester picked from their own device. The live pipeline
 * will read the real content; for now the understanding is an honest
 * stand-in so pressure-testers can run ANY document through the flow.
 */
export const makeUserDocument = (file: File): CapturedDocument => {
  userFileCounter += 1;
  const isImage = file.type.startsWith('image/');
  return {
    id: `user-file-${userFileCounter}`,
    fileName: file.name,
    fileSizeKb: Math.max(1, Math.round(file.size / 1024)),
    pageCount: 1,
    tier: 'generic',
    classification: 'Care document',
    pageImages: isImage ? [URL.createObjectURL(file)] : [],
    summary: [
      `You uploaded "${file.name}" from this device.`,
      'In the live product, Keni reads the actual content of your document. This prototype shows a stand-in understanding so you can feel the full flow with your own files.',
    ],
    meaning: [
      'Whatever the document turns out to be, Keni keeps the guidance inside known care pathways and your care manager can always pick it up with you.',
    ],
    suggestedActions: [
      {
        id: `user-${userFileCounter}-cam`,
        title: 'Talk to your care manager about this document',
        description:
          'They can read it with you and agree what, if anything, needs doing.',
      },
      {
        id: `user-${userFileCounter}-store`,
        title: 'Keep the original somewhere safe',
        description: 'A copy now lives in your KareHero documents either way.',
      },
    ],
    isUserProvided: true,
  };
};

/** Deep tier 1: POA/LPA. Captured via the photo multi-shot path. */
export const lpaDocument: CapturedDocument = {
  id: 'lpa-margaret',
  fileName: 'lpa_health_welfare.jpeg',
  fileSizeKb: 412,
  pageCount: 4,
  tier: 'deep',
  classification: 'Lasting Power of Attorney (Health & Welfare)',
  pageImages: [lpaPage1, lpaPage2, lpaPage3, lpaPage4],
  summary: [
    'This is a registered Health & Welfare LPA for Margaret Patrick (your mum).',
    'It names you, Olivia Patrick, as her attorney, with Helen Patrick as a replacement attorney.',
    'It was registered with the Office of the Public Guardian on 3 March 2026 (reference 7000-2841-9637).',
    'Margaret has chosen to let her attorneys decide about life-sustaining treatment.',
  ],
  meaning: [
    'Because it is registered, you can use this LPA straight away if Margaret loses the ability to make her own health and care decisions.',
    'Care homes, hospitals and GPs may ask to see it before they involve you in decisions, so it helps to have copies ready.',
    'Until Margaret loses capacity, she still makes her own decisions - the LPA waits in the background.',
  ],
  suggestedActions: [
    {
      id: 'lpa-gp',
      title: "Share a copy with Margaret's GP surgery",
      description:
        'GP surgeries keep a copy on file so they can involve you in decisions without delays.',
    },
    {
      id: 'lpa-original',
      title: 'Store the original safely and tell Helen where it is',
      description:
        'As replacement attorney, Helen may need to find it quickly one day.',
    },
    {
      id: 'lpa-online',
      title: "Set up the OPG's online LPA service",
      description:
        'Use the activation key on the registration letter to share the LPA digitally instead of posting certified copies.',
    },
    {
      id: 'lpa-carehome',
      title: "Tell Margaret's care home the LPA is registered",
      description:
        'They will record you as the health and welfare decision-maker on her care plan.',
    },
  ],
};

/** Deep tier 2: benefits/DWP paperwork. Captured via the file upload path. */
export const dwpDocument: CapturedDocument = {
  id: 'dwp-aa-letter',
  fileName: 'aa-decision-letter.pdf',
  fileSizeKb: 186,
  pageCount: 3,
  tier: 'deep',
  classification: 'DWP decision letter (Attendance Allowance)',
  pageImages: [dwpLetter],
  summary: [
    'The DWP has awarded Margaret Patrick Attendance Allowance at the higher rate of £110.40 a week.',
    'Payments start from 18 May 2026 and are paid every 4 weeks into her bank account.',
    'The award will be looked at again on 18 May 2028.',
  ],
  meaning: [
    "Attendance Allowance is not means-tested and does not reduce Margaret's State Pension.",
    'The award can unlock other support: you may now qualify for Carer’s Allowance, and Margaret may get more Pension Credit or a Council Tax reduction.',
    'Margaret can spend it however helps her most - it does not have to go to a registered carer.',
  ],
  suggestedActions: [
    {
      id: 'dwp-carers-allowance',
      title: 'Check whether you can now claim Carer’s Allowance',
      description:
        'Caring 35+ hours a week for someone on Attendance Allowance usually qualifies. Worth £83.30 a week.',
    },
    {
      id: 'dwp-council',
      title: "Tell Margaret's council about the award",
      description:
        'It can increase her Pension Credit or Council Tax support - these do not update automatically.',
    },
    {
      id: 'dwp-review-date',
      title: 'Add the May 2028 review date to your calendar',
      description:
        "The DWP will write before then, but a reminder means it won't catch you out.",
    },
  ],
};

/** Generic tier: discharge summary. Shows capture accepts any care document. */
export const dischargeDocument: CapturedDocument = {
  id: 'discharge-summary',
  fileName: 'discharge-summary.pdf',
  fileSizeKb: 224,
  pageCount: 2,
  tier: 'generic',
  classification: 'Hospital discharge summary',
  pageImages: [dischargeSummary],
  summary: [
    'Margaret was admitted on 26 May after a fall at home, with a urinary tract infection that has now been treated.',
    'She was discharged home on 2 June with family support. No fractures, some bruising on her left hip.',
    'Her medication changed: Adcal-D3 started for bone protection, Zopiclone stopped because it increases falls risk.',
    'The hospital has referred her to the community falls prevention service.',
  ],
  meaning: [
    'The key follow-up is a GP medication review within 2 weeks of discharge.',
    'The falls team will contact Margaret directly - you do not need to chase them yet.',
    'The hospital suggests considering a pendant alarm and bathroom grab rails to reduce the risk of another fall.',
  ],
  suggestedActions: [
    {
      id: 'dis-gp-review',
      title: 'Book a GP medication review for Margaret',
      description:
        'The hospital asks for this within 2 weeks of discharge (by 16 June).',
    },
    {
      id: 'dis-falls-alarm',
      title: 'Look into a pendant alarm and grab rails',
      description:
        'The discharge team recommends both to reduce the risk of another fall at home.',
    },
    {
      id: 'dis-tell-cam',
      title: 'Tell your care manager about the hospital stay',
      description:
        "They can reassess Margaret's care plan around the new falls risk.",
    },
  ],
};

/** Demo of the failure path: an unreadable capture. */
export const blurryDocument: CapturedDocument = {
  id: 'blurry-scan',
  fileName: 'scan_0231.jpeg',
  fileSizeKb: 96,
  pageCount: 1,
  tier: 'generic',
  classification: 'Unknown',
  pageImages: [],
  summary: [],
  meaning: [],
  suggestedActions: [],
  willFail: true,
  failReason:
    "We couldn't read this document clearly. It might be blurry, cropped, or too dark.",
};

/** What the mock photo gallery offers. */
export const galleryChoices = [lpaDocument, blurryDocument];

/** What the mock file picker offers. */
export const fileChoices = [dwpDocument, dischargeDocument, blurryDocument];

/** The photo multi-shot path always resolves to the LPA fixture. */
export const photoSessionDocument = lpaDocument;
